/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { ContextualMenuItemType, DirectionalHint, IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { getId } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { CollapsableSearchBoxProps } from 'src/core/components/CollapsableSearchBox';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { ctrlKey } from 'src/core/utils/keyboardUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { utteranceTableCommandBarMessages as messages } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceTableCommandBar/UtteranceTableCommandBarMessages';
import { UtterancesTableCommandBarProps } from 'src/luis/projects/utterances/components/UtteranceTable/UtteranceTableCommandBar/UtteranceTableCommandBarProps';
import { UtteranceTableViewOptions } from 'src/luis/projects/utterances/types/UtteranceTableViewOptions';
import { utterancesTeachingBubbleMessages } from 'src/luis/projects/utterances/utils/utterancesTeachingBubbleMessages';

/**
 * Gets the commands to display in the command bar based on the
 * selected items.
 *
 * @param commands All the commands supported.
 * @param selectedItemsLength Total number of selected items.
 * @returns An array of filtered commands.
 */
const getCommandBarItems = (
    commands: ICommandBarItemProps[],
    selectedItemsLength: number,
    isConfirmAllPredictionsDisabled: boolean
): ICommandBarItemProps[] => {
    const updatedCommands: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['confirmEntityPredictions'],
        ['confirmEntityPredictions', 'edit', 'add', 'moveToIntent', 'delete'],
        ['confirmEntityPredictions', 'moveToIntent', 'delete'],
        selectedItemsLength
    );

    /**
     * Ensures that confirm all predictions command will be
     * disabled if there is no ML entities predicted.
     */
    if (isConfirmAllPredictionsDisabled) {
        updatedCommands.find(c => c.key === 'confirmEntityPredictions').disabled = true;
    }

    return updatedCommands;
};

/**
 * Represents a command bar for the utterances table.
 */
export const UtteranceTableCommandBar = React.memo((props: UtterancesTableCommandBarProps) => {
    const {
        selectedView,
        areFiltersApplied,
        isAddToPatternsDisabled,
        isConfirmAllPredictionsDisabled,
        onAddToPatterns: onAddAsPattern,
        onChangeViewOptions,
        onConfirmEntityPredictions,
        onDelete,
        onEdit,
        onQueryChange,
        onMoveToIntent,
        onToggleEntityPalette,
        onToggleFiltersPane,
        query,
        reAssignableIntents,
        selectedItemsLength,
        viewOptions,
        userRole,
        displayedUserRole,
        componentRef
    } = props;
    const { localize } = useLocalization();
    const { uiStore } = useGlobalState();
    const eventLogger = getEventLogger('UtteranceTableCommandBar');

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);
    const entityPaletteIdRef = React.useRef<string>(getId());

    React.useEffect(() => () => dequeueTeachingBubble('EntityPalette'), []);

    React.useEffect(() => {
        if (!uiStore.teachingBubbles.EntityPalette) {
            createTeachingBubble(
                'EntityPalette',
                localize(utterancesTeachingBubbleMessages.entityPaletteBody),
                localize(utterancesTeachingBubbleMessages.entityPaletteTitle),
                `#${entityPaletteIdRef.current}`,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles)]);

    useDocumentEventHandler('keydown', event => {
        if (ctrlKey(event) && event.key.toLowerCase() === 'e') {
            event.preventDefault();
            if (event.shiftKey) {
                onChangeViewOptions({ hideSubEntities: !viewOptions.hideSubEntities });
            } else {
                onChangeViewOptions({ hideEntityNames: !viewOptions.hideEntityNames });
            }
        }
    });

    const handleSearchBoxChange: CollapsableSearchBoxProps['onChange'] = (_, newValue?: string) => onQueryChange(newValue);

    const handleEntityPaletteIconClick = () => {
        onToggleEntityPalette();
        dequeueTeachingBubble('EntityPalette');
    };

    const reassignIntentItems: IContextualMenuItem[] = reAssignableIntents.map(intent => ({
        key: intent.name,
        text: intent.name,
        onClick: () => {
            onMoveToIntent(intent.id);
            eventLogger.log('ReassignIntentCommandBarOption');
        },
        'data-automation-id': `reassignIntent-${intent.name}`
    }));

    const getIconsProps = (viewOption: keyof UtteranceTableViewOptions) => ({
        iconName: 'checkmark',
        styles: { root: { opacity: viewOptions[viewOption] ? 0 : 1, color: 'black' } }
    });

    const handleViewOptionClick = (viewOption: keyof UtteranceTableViewOptions) => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        onChangeViewOptions({ [viewOption]: !viewOptions[viewOption] });
    };

    // ------------------------ CommandBar config  ------------------------

    const viewOptionsSubMenu: IContextualMenuProps = {
        styles: { root: { minWidth: 320 } },
        directionalHint: DirectionalHint.bottomRightEdge,
        items: [
            {
                key: 'intents',
                itemType: ContextualMenuItemType.Header,
                text: localize(messages.intents),
                onRenderIcon: () => null
            },
            {
                key: 'intentDetailedScores',
                'data-automation-id': 'intentDetailedScores',
                text: localize(messages.intentDetailedScores),
                iconProps: getIconsProps('hideIntentDetailedScores'),
                onClick: handleViewOptionClick('hideIntentDetailedScores')
            },
            {
                key: 'entities',
                itemType: ContextualMenuItemType.Header,
                text: localize(messages.entities),
                onRenderIcon: () => null
            },
            {
                key: 'entityPredictions',
                'data-automation-id': 'entityPredictions',
                text: localize(messages.entityPredictions),
                iconProps: getIconsProps('hidePredictions'),
                onClick: handleViewOptionClick('hidePredictions')
            },
            {
                key: 'entityNames',
                'data-automation-id': 'entityNames',
                text: localize(messages.entityNames),
                secondaryText: 'Ctrl+E',
                iconProps: getIconsProps('hideEntityNames'),
                onClick: handleViewOptionClick('hideEntityNames')
            },
            {
                key: 'subentities',
                'data-automation-id': 'subentities',
                text: localize(messages.subentities),
                secondaryText: 'Shift+Ctrl+E',
                iconProps: getIconsProps('hideSubEntities'),
                onClick: handleViewOptionClick('hideSubEntities')
            },
            {
                key: 'prebuiltRegexEntities',
                'data-automation-id': 'prebuiltRegexEntities',
                text: localize(messages.prebuiltRegexEntities),
                iconProps: getIconsProps('hideRegexAndPrebuilt'),
                onClick: handleViewOptionClick('hideRegexAndPrebuilt')
            }
        ]
    };

    const commands: ICommandBarItemProps[] = [
        {
            key: 'confirmEntityPredictions',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="confirmEntityPredictionsButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onConfirmEntityPredictions}
                            iconProps={{ iconName: 'checkMark' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            text={
                                selectedItemsLength > 0
                                    ? localize(messages.confirmEntityPredictions)
                                    : localize(messages.confirmEntityPredictionsAll)
                            }
                            data-automation-id="confirmEntityPredictionsButton"
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'moveToIntent',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="moveToIntentButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            name={localize(messages.moveToIntent)}
                            text={localize(messages.moveToIntent)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            data-automation-id="moveToIntentButton"
                            disabled={!canWrite || item.disabled}
                            iconProps={{ iconName: 'MoveToFolder' }}
                            split
                            menuProps={{
                                items: reassignIntentItems
                            }}
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canDeleteData || item.disabled}
                            onClick={() => {
                                onDelete();
                                eventLogger.log('DeleteUtteranceCommandBarOption');
                            }}
                            iconProps={{ iconName: 'Delete' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            text={localize(messages.delete)}
                            data-automation-id="deleteButton"
                        />
                    }
                />
            ),
            disabled: false
        }
    ];

    const overflowCommands: ICommandBarItemProps[] = [
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={() => {
                                onEdit();
                                eventLogger.log('EditUtteranceCommandBarOption');
                            }}
                            iconProps={{ iconName: 'Edit' }}
                            styles={getCommandBarButtonStyles(true)}
                            name={localize(messages.editUtterance)}
                            text={localize(messages.editUtterance)}
                            data-automation-id="editButton"
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'addToPatterns',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addToPatternsButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={() => {
                                onAddAsPattern();
                                eventLogger.log('AddToPatternsCommandBarOption');
                            }}
                            iconProps={{ iconName: 'Code' }}
                            styles={getCommandBarButtonStyles(true)}
                            name={localize(messages.addToPatterns)}
                            text={localize(messages.addToPatterns)}
                            data-automation-id="addToPatternsButton"
                        />
                    }
                />
            ),
            disabled: false
        }
    ];

    const farCommands: ICommandBarItemProps[] = [
        {
            key: 'search',
            iconProps: { iconName: 'Search' },
            iconOnly: true,
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    value={query}
                    ariaLabel={localize(messages.search)}
                    placeholder={localize(messages.search)}
                    onChange={handleSearchBoxChange}
                />
            )
        },
        {
            key: 'viewOptions',
            iconProps: { iconName: 'List' },
            text: localize(messages.viewOptions),
            subMenuProps: viewOptionsSubMenu,
            'data-automation-id': 'utteranceTableViewOptions'
        },
        {
            key: 'filter',
            iconProps: { iconName: areFiltersApplied ? 'FilterSolid' : 'Filter' },
            iconOnly: true,
            ariaLabel: localize(messages.filterAriaLabel),
            checked: selectedView === 'viewOptions',
            onClick: onToggleFiltersPane,
            'data-automation-id': 'utteranceTableFilters',
            title: localize(messages.filter)
        },
        {
            key: 'entityPalette',
            id: entityPaletteIdRef.current,
            iconProps: { iconName: 'Accounts' },
            ariaLabel: localize(messages.entityPaletteBtnAriaLabel),
            iconOnly: true,
            checked: selectedView === 'entityPalette',
            onClick: handleEntityPaletteIconClick,
            'data-automation-id': 'entityPalette',
            disabled: !canWrite,
            title: localize(messages.entityPalette)
        }
    ];

    const items = getCommandBarItems(commands, selectedItemsLength, isConfirmAllPredictionsDisabled);

    const overflowItems = commandBarItemSelectionManager(
        overflowCommands,
        [],
        ['edit', ...(!isAddToPatternsDisabled ? ['addToPatterns'] : [])],
        [],
        selectedItemsLength
    );

    return <LuisCommandBar items={items} farItems={farCommands} overflowItems={overflowItems} componentRef={componentRef} />;
});

UtteranceTableCommandBar.displayName = 'UtteranceTableCommandBar';
